// Quest UI - Turkish Mythology ARPG
// Shows quest log and tracking - Responsive Design

import { Renderer } from '@client/core/Renderer';
import { Quest } from '@shared/quests/Quest';
import { KEY_BINDINGS } from '@shared/constants/KeyBindings';
import { t } from '@locales/index';

export class QuestUI {
  private isOpen: boolean = false;
  private quests: Quest[] = [];
  private selectedQuestIndex: number = 0;
  private selectedTab: 'active' | 'available' | 'completed' = 'active';

  private width: number;
  private height: number;

  // Base dimensions (will scale)
  private readonly BASE_PANEL_WIDTH = 700;
  private readonly BASE_PANEL_HEIGHT = 500;

  // Animation
  private fadeAlpha: number = 0;

  // Tracked quest for HUD display
  private trackedQuestId: string | null = null;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  // Responsive scaling
  private get scale(): number {
    const widthScale = this.width / 1280;
    const heightScale = this.height / 720;
    return Math.max(0.6, Math.min(1, Math.min(widthScale, heightScale)));
  }

  private get panelWidth(): number {
    return Math.min(this.BASE_PANEL_WIDTH * this.scale, this.width - 40);
  }

  private get panelHeight(): number {
    return Math.min(this.BASE_PANEL_HEIGHT * this.scale, this.height - 40);
  }

  private get fontSize(): { title: number; large: number; medium: number; small: number; tiny: number } {
    return {
      title: Math.max(16, 24 * this.scale),
      large: Math.max(14, 18 * this.scale),
      medium: Math.max(11, 14 * this.scale),
      small: Math.max(10, 12 * this.scale),
      tiny: Math.max(9, 11 * this.scale),
    };
  }

  setScreenSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.fadeAlpha = 0;
    }
  }

  open(): void {
    this.isOpen = true;
    this.fadeAlpha = 0;
  }

  close(): void {
    this.isOpen = false;
  }

  isVisible(): boolean {
    return this.isOpen;
  }

  updateQuests(quests: Quest[]): void {
    this.quests = quests;
  }

  getTrackedQuestId(): string | null {
    return this.trackedQuestId;
  }

  trackQuest(questId: string | null): void {
    this.trackedQuestId = questId;
  }

  update(deltaTime: number): void {
    if (!this.isOpen) return;

    if (this.fadeAlpha < 1) {
      this.fadeAlpha += deltaTime * 5;
      if (this.fadeAlpha > 1) this.fadeAlpha = 1;
    }
  }

  handleKeyDown(key: string): boolean {
    if (!this.isOpen) return false;

    const filteredQuests = this.getFilteredQuests();

    switch (key) {
      case 'Escape':
      case 'j':
      case 'J':
        this.close();
        return true;

      case 'ArrowUp':
        this.selectedQuestIndex = Math.max(0, this.selectedQuestIndex - 1);
        return true;

      case 'ArrowDown':
        this.selectedQuestIndex = Math.min(filteredQuests.length - 1, this.selectedQuestIndex + 1);
        return true;

      case 'ArrowLeft':
        this.switchTab(-1);
        return true;

      case 'ArrowRight':
        this.switchTab(1);
        return true;

      case 't':
      case 'T':
        if (filteredQuests[this.selectedQuestIndex]) {
          const quest = filteredQuests[this.selectedQuestIndex];
          if (quest.status === 'active') {
            this.trackedQuestId = this.trackedQuestId === quest.id ? null : quest.id;
          }
        }
        return true;

      case '1':
        this.selectedTab = 'active';
        this.selectedQuestIndex = 0;
        return true;

      case '2':
        this.selectedTab = 'available';
        this.selectedQuestIndex = 0;
        return true;

      case '3':
        this.selectedTab = 'completed';
        this.selectedQuestIndex = 0;
        return true;
    }

    return false;
  }

  handleClick(x: number, y: number): boolean {
    if (!this.isOpen) return false;

    const panelX = (this.width - this.panelWidth) / 2;
    const panelY = (this.height - this.panelHeight) / 2;

    // Check if click is outside panel
    if (x < panelX || x > panelX + this.panelWidth ||
        y < panelY || y > panelY + this.panelHeight) {
      this.close();
      return true;
    }

    // Check tab clicks
    const tabY = panelY + 45 * this.scale;
    const tabWidth = 100 * this.scale;
    const tabHeight = 30 * this.scale;
    const tabsStartX = panelX + 20 * this.scale;

    if (y >= tabY && y <= tabY + tabHeight) {
      if (x >= tabsStartX && x <= tabsStartX + tabWidth) {
        this.selectedTab = 'active';
        this.selectedQuestIndex = 0;
        return true;
      } else if (x >= tabsStartX + tabWidth + 10 * this.scale && x <= tabsStartX + tabWidth * 2 + 10 * this.scale) {
        this.selectedTab = 'available';
        this.selectedQuestIndex = 0;
        return true;
      } else if (x >= tabsStartX + tabWidth * 2 + 20 * this.scale && x <= tabsStartX + tabWidth * 3 + 20 * this.scale) {
        this.selectedTab = 'completed';
        this.selectedQuestIndex = 0;
        return true;
      }
    }

    // Check quest list clicks
    const listStartY = panelY + 90 * this.scale;
    const itemHeight = 50 * this.scale;
    const filteredQuests = this.getFilteredQuests();
    const maxVisible = Math.floor((this.panelHeight - 120 * this.scale) / itemHeight);

    for (let i = 0; i < filteredQuests.length && i < maxVisible; i++) {
      const itemY = listStartY + i * itemHeight;
      if (y >= itemY && y <= itemY + itemHeight - 5 * this.scale) {
        this.selectedQuestIndex = i;
        return true;
      }
    }

    return true;
  }

  private switchTab(direction: number): void {
    const tabs: ('active' | 'available' | 'completed')[] = ['active', 'available', 'completed'];
    const currentIndex = tabs.indexOf(this.selectedTab);
    const newIndex = (currentIndex + direction + 3) % 3;
    this.selectedTab = tabs[newIndex];
    this.selectedQuestIndex = 0;
  }

  private getFilteredQuests(): Quest[] {
    return this.quests.filter(q => q.status === this.selectedTab);
  }

  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    const ctx = renderer.context;
    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    // Darken background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, this.width, this.height);

    const panelX = (this.width - this.panelWidth) / 2;
    const panelY = (this.height - this.panelHeight) / 2;

    // Panel background
    ctx.fillStyle = 'rgba(20, 15, 30, 0.95)';
    ctx.strokeStyle = '#6a4a8a';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, this.panelWidth, this.panelHeight, 12 * this.scale);
    ctx.fill();
    ctx.stroke();

    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${this.fontSize.title}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(t('questLog.title'), panelX + this.panelWidth / 2, panelY + 30 * this.scale);

    // Tabs
    this.renderTabs(ctx, panelX, panelY);

    // Quest list (left side)
    this.renderQuestList(ctx, panelX, panelY);

    // Quest details (right side)
    this.renderQuestDetails(ctx, panelX, panelY);

    // Instructions
    ctx.fillStyle = '#888888';
    ctx.font = `${this.fontSize.tiny}px Arial`;
    ctx.textAlign = 'center';
    const keyHint = `[${KEY_BINDINGS.MENU_QUESTS.display}] Kapat  [1/2/3] Sekmeler  [↑/↓] Seç  [T] Takip Et`;
    ctx.fillText(keyHint, panelX + this.panelWidth / 2, panelY + this.panelHeight - 12 * this.scale);

    ctx.restore();
  }

  private renderTabs(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const tabY = panelY + 45 * this.scale;
    const tabWidth = 100 * this.scale;
    const tabHeight = 28 * this.scale;
    const tabsStartX = panelX + 20 * this.scale;
    const tabSpacing = 8 * this.scale;

    const tabs = [
      { id: 'active', label: t('questLog.active'), count: this.quests.filter(q => q.status === 'active').length },
      { id: 'available', label: t('questLog.available'), count: this.quests.filter(q => q.status === 'available').length },
      { id: 'completed', label: t('questLog.completed'), count: this.quests.filter(q => q.status === 'completed').length }
    ];

    tabs.forEach((tab, i) => {
      const tabX = tabsStartX + i * (tabWidth + tabSpacing);
      const isSelected = this.selectedTab === tab.id;

      // Tab background
      ctx.fillStyle = isSelected ? 'rgba(100, 70, 130, 0.8)' : 'rgba(50, 40, 60, 0.6)';
      ctx.beginPath();
      ctx.roundRect(tabX, tabY, tabWidth, tabHeight, 5 * this.scale);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#9966cc';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Tab label
      ctx.fillStyle = isSelected ? '#ffffff' : '#aaaaaa';
      ctx.font = isSelected ? `bold ${this.fontSize.small}px Arial` : `${this.fontSize.small}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(`${tab.label} (${tab.count})`, tabX + tabWidth / 2, tabY + tabHeight / 2 + 4 * this.scale);
    });
  }

  private renderQuestList(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const listX = panelX + 20 * this.scale;
    const listY = panelY + 85 * this.scale;
    const listWidth = this.panelWidth * 0.38;
    const itemHeight = 48 * this.scale;
    const maxVisible = Math.floor((this.panelHeight - 130 * this.scale) / itemHeight);

    const filteredQuests = this.getFilteredQuests();

    if (filteredQuests.length === 0) {
      ctx.fillStyle = '#666666';
      ctx.font = `${this.fontSize.medium}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(t('questLog.noQuests'), listX + 10 * this.scale, listY + 25 * this.scale);
      return;
    }

    for (let i = 0; i < filteredQuests.length && i < maxVisible; i++) {
      const quest = filteredQuests[i];
      const itemY = listY + i * itemHeight;
      const isSelected = i === this.selectedQuestIndex;
      const isTracked = quest.id === this.trackedQuestId;

      // Item background
      ctx.fillStyle = isSelected ? 'rgba(80, 60, 100, 0.8)' : 'rgba(40, 30, 50, 0.5)';
      ctx.beginPath();
      ctx.roundRect(listX, itemY, listWidth, itemHeight - 4 * this.scale, 5 * this.scale);
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#8866aa';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Act indicator
      ctx.fillStyle = this.getActColor(quest.act);
      ctx.font = `bold ${this.fontSize.tiny}px Arial`;
      ctx.textAlign = 'left';
      ctx.fillText(`ACT ${quest.act}`, listX + 8 * this.scale, itemY + 14 * this.scale);

      // Quest name
      ctx.fillStyle = isTracked ? '#ffcc00' : '#ffffff';
      ctx.font = isSelected ? `bold ${this.fontSize.small}px Arial` : `${this.fontSize.small}px Arial`;

      let questName = quest.name;
      if (questName.length > 20) {
        questName = questName.substring(0, 18) + '...';
      }
      ctx.fillText(questName, listX + 8 * this.scale, itemY + 30 * this.scale);

      // Progress indicator
      if (quest.status === 'active') {
        const progress = this.getQuestProgress(quest);

        // Progress bar
        const barWidth = 50 * this.scale;
        const barX = listX + listWidth - barWidth - 8 * this.scale;
        const barY = itemY + 28 * this.scale;
        ctx.fillStyle = '#333333';
        ctx.fillRect(barX, barY, barWidth, 5 * this.scale);
        ctx.fillStyle = '#66aa66';
        ctx.fillRect(barX, barY, barWidth * progress, 5 * this.scale);

        ctx.fillStyle = '#888888';
        ctx.font = `${this.fontSize.tiny}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText(`${Math.round(progress * 100)}%`, barX - 4 * this.scale, barY + 5 * this.scale);
      }

      // Tracked indicator
      if (isTracked) {
        ctx.fillStyle = '#ffcc00';
        ctx.font = `${this.fontSize.small}px Arial`;
        ctx.textAlign = 'right';
        ctx.fillText('★', listX + listWidth - 8 * this.scale, itemY + 15 * this.scale);
      }
    }
  }

  private renderQuestDetails(ctx: CanvasRenderingContext2D, panelX: number, panelY: number): void {
    const detailsX = panelX + this.panelWidth * 0.42;
    const detailsY = panelY + 85 * this.scale;
    const detailsWidth = this.panelWidth * 0.55;
    const detailsHeight = this.panelHeight - 130 * this.scale;

    // Details background
    ctx.fillStyle = 'rgba(30, 25, 40, 0.8)';
    ctx.beginPath();
    ctx.roundRect(detailsX, detailsY, detailsWidth, detailsHeight, 8 * this.scale);
    ctx.fill();

    const filteredQuests = this.getFilteredQuests();
    const quest = filteredQuests[this.selectedQuestIndex];

    if (!quest) {
      ctx.fillStyle = '#666666';
      ctx.font = `${this.fontSize.medium}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(t('questLog.noSelection'), detailsX + detailsWidth / 2, detailsY + detailsHeight / 2);
      return;
    }

    const padding = 15 * this.scale;
    const lineHeight = 18 * this.scale;
    let yOffset = detailsY + 20 * this.scale;

    // Quest name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${this.fontSize.large}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(quest.name, detailsX + padding, yOffset);
    yOffset += lineHeight * 1.2;

    // Act and chapter
    ctx.fillStyle = this.getActColor(quest.act);
    ctx.font = `${this.fontSize.small}px Arial`;
    ctx.fillText(t('questLog.actChapter', { act: quest.act, chapter: quest.chapter }), detailsX + padding, yOffset);
    yOffset += lineHeight * 1.2;

    // Description
    ctx.fillStyle = '#cccccc';
    ctx.font = `${this.fontSize.small}px Arial`;
    this.wrapText(ctx, quest.description, detailsX + padding, yOffset, detailsWidth - padding * 2, lineHeight * 0.9);
    yOffset += lineHeight * 2.5;

    // Objectives
    ctx.fillStyle = '#aaaaaa';
    ctx.font = `bold ${this.fontSize.medium}px Arial`;
    ctx.fillText(t('questLog.objectives'), detailsX + padding, yOffset);
    yOffset += lineHeight;

    for (const objective of quest.objectives) {
      if (yOffset > detailsY + detailsHeight - 80 * this.scale) break;

      const isComplete = objective.completed;
      ctx.fillStyle = isComplete ? '#66aa66' : '#ffffff';
      ctx.font = `${this.fontSize.small}px Arial`;

      const checkMark = isComplete ? '✓' : '○';
      const progress = objective.type === 'reach_zone' || objective.type === 'talk_to_npc'
        ? ''
        : ` (${objective.current}/${objective.required})`;

      ctx.fillText(`${checkMark} ${objective.description}${progress}`, detailsX + padding + 5 * this.scale, yOffset);
      yOffset += lineHeight * 0.95;
    }

    yOffset += lineHeight * 0.5;

    // Rewards
    if (yOffset < detailsY + detailsHeight - 60 * this.scale) {
      ctx.fillStyle = '#aaaaaa';
      ctx.font = `bold ${this.fontSize.medium}px Arial`;
      ctx.fillText(t('questLog.rewards'), detailsX + padding, yOffset);
      yOffset += lineHeight;

      ctx.font = `${this.fontSize.small}px Arial`;
      if (quest.rewards.exp) {
        ctx.fillStyle = '#88ccff';
        ctx.fillText(`• ${quest.rewards.exp} Deneyim`, detailsX + padding + 5 * this.scale, yOffset);
        yOffset += lineHeight * 0.9;
      }
      if (quest.rewards.gold) {
        ctx.fillStyle = '#ffcc44';
        ctx.fillText(`• ${t('questLog.goldReward', { amount: quest.rewards.gold })}`, detailsX + padding + 5 * this.scale, yOffset);
        yOffset += lineHeight * 0.9;
      }
      if (quest.rewards.skillPoints) {
        ctx.fillStyle = '#66ff66';
        ctx.fillText(`• ${t('questLog.skillPointReward', { amount: quest.rewards.skillPoints })}`, detailsX + padding + 5 * this.scale, yOffset);
        yOffset += lineHeight * 0.9;
      }
      if (quest.rewards.passivePoints) {
        ctx.fillStyle = '#ff66ff';
        ctx.fillText(`• ${t('questLog.passivePointReward', { amount: quest.rewards.passivePoints })}`, detailsX + padding + 5 * this.scale, yOffset);
      }
    }

    // Track button hint for active quests
    if (quest.status === 'active') {
      const isTracked = quest.id === this.trackedQuestId;
      ctx.fillStyle = isTracked ? '#ffcc00' : '#888888';
      ctx.font = `${this.fontSize.tiny}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(
        isTracked ? `★ ${t('questLog.tracking')}` : t('questLog.trackPrompt'),
        detailsX + detailsWidth / 2,
        detailsY + detailsHeight - 15 * this.scale
      );
    }
  }

  // Render tracked quest on HUD (also responsive)
  renderTrackedQuest(renderer: Renderer, x: number, y: number): void {
    if (!this.trackedQuestId) return;

    const quest = this.quests.find(q => q.id === this.trackedQuestId);
    if (!quest || quest.status !== 'active') {
      this.trackedQuestId = null;
      return;
    }

    const ctx = renderer.context;
    ctx.save();

    const boxWidth = Math.min(220, this.width * 0.2);
    const lineHeight = 16 * this.scale;
    const boxHeight = (20 + quest.objectives.length * 16) * this.scale;

    // Background
    ctx.fillStyle = 'rgba(20, 15, 30, 0.85)';
    ctx.strokeStyle = '#6a4a8a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, boxWidth, boxHeight, 6 * this.scale);
    ctx.fill();
    ctx.stroke();

    // Quest name
    ctx.fillStyle = '#ffcc00';
    ctx.font = `bold ${Math.max(10, 11 * this.scale)}px Arial`;
    ctx.textAlign = 'left';
    ctx.fillText(quest.name, x + 8 * this.scale, y + 14 * this.scale);

    // Objectives
    let objY = y + 28 * this.scale;
    for (const obj of quest.objectives) {
      const isComplete = obj.completed;
      ctx.fillStyle = isComplete ? '#66aa66' : '#cccccc';
      ctx.font = `${Math.max(9, 10 * this.scale)}px Arial`;

      const checkMark = isComplete ? '✓' : '○';
      const progress = obj.type === 'reach_zone' || obj.type === 'talk_to_npc'
        ? ''
        : ` ${obj.current}/${obj.required}`;

      let text = `${checkMark} ${obj.description}${progress}`;
      const maxChars = Math.floor(boxWidth / (6 * this.scale));
      if (text.length > maxChars) {
        text = text.substring(0, maxChars - 3) + '...';
      }

      ctx.fillText(text, x + 8 * this.scale, objY);
      objY += lineHeight;
    }

    ctx.restore();
  }

  private getQuestProgress(quest: Quest): number {
    let totalRequired = 0;
    let totalCurrent = 0;

    for (const obj of quest.objectives) {
      totalRequired += obj.required;
      totalCurrent += obj.current;
    }

    return totalRequired > 0 ? totalCurrent / totalRequired : 0;
  }

  private getActColor(act: number): string {
    switch (act) {
      case 1: return '#66aa66';
      case 2: return '#6688cc';
      case 3: return '#aa66aa';
      case 4: return '#cc6666';
      default: return '#888888';
    }
  }

  private wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number): void {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);

      if (metrics.width > maxWidth && line !== '') {
        ctx.fillText(line.trim(), x, currentY);
        line = word + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }

    if (line.trim()) {
      ctx.fillText(line.trim(), x, currentY);
    }
  }
}
